import { Calendar, Callout, DateRangeType, DayOfWeek, DefaultButton, DirectionalHint, IconButton, Stack, Text, useTheme } from '@fluentui/react';
import { getISOWeek } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Period } from '../api/generated/data-contracts';
import { usePeriodPickerTranslations } from '../hooks/usePriodPickerTranslations';
import { getPeriodWithDate } from './getPeriodWithDate';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../api/api';
import { MISSING_APPROVAL_PERIODS } from '../constants/queryKeys';

const useTranslatePeriodName = () => {
    const { t } = useTranslation();
    const getPeriodName = useCallback(
        (period: Period) => {
            const periodStart = new Date(period.startDate);
            const periodEnd = new Date(period.endDate);
            const startMonthName = t(`common:shortMonths.${periodStart.getMonth()}`);
            const endMonthName = t(`common:shortMonths.${periodEnd.getMonth()}`);
            const weekNumber = getISOWeek(periodStart);
            return t('common:previewPeriodName', {
                startDate: periodStart.getDate(),
                startMonth: startMonthName,
                endDate: periodEnd.getDate(),
                endMonth: endMonthName,
                week: weekNumber,
            });
        },
        [t],
    );
    return getPeriodName;
};

export const PeriodPicker = ({
    allPeriods,
    selectedPeriod,
    setSelectedPeriod,
}: {
    allPeriods: Period[];
    setSelectedPeriod: (period?: Period | null) => void;
    selectedPeriod?: Period | null;
}) => {
    const theme = useTheme();
    const calendarButtonRef = useRef(null);
    const calendarStrings = usePeriodPickerTranslations();
    const { t } = useTranslation();
    const [value, setValue] = useState(() => new Date());

    const getPeriodName = useTranslatePeriodName();

    const periodName = useMemo(() => {
        if (!selectedPeriod) {
            return t('approval:table.item.state.notCreated');
        }
        return getPeriodName(selectedPeriod);
    }, [getPeriodName, selectedPeriod, t]);

    const [showCallout, setShowCallout] = useState(false);

    const handleShowCallout = useCallback(() => setShowCallout(true), []);
    const handleHideCallout = useCallback(() => setShowCallout(false), []);

    const handleNextPeriod = useCallback(() => {
        const currentPeriodIndex = allPeriods.findIndex(period => period.id === selectedPeriod?.id);
        if (currentPeriodIndex + 1 === allPeriods.length) {
            setSelectedPeriod(null);
        } else {
            const nextPeriod = allPeriods[currentPeriodIndex + 1];
            setSelectedPeriod(nextPeriod);
            setValue(new Date(nextPeriod.startDate));
        }
    }, [allPeriods, selectedPeriod?.id, setSelectedPeriod]);

    const handlePrevPeriod = useCallback(() => {
        const currentPeriodIndex = allPeriods.findIndex(period => period.id === selectedPeriod?.id);
        if (currentPeriodIndex === 0) {
            setSelectedPeriod(null);
        } else {
            const prevPeriod = allPeriods[currentPeriodIndex - 1];
            setSelectedPeriod(prevPeriod);
            setValue(new Date(prevPeriod.startDate));
        }
    }, [allPeriods, selectedPeriod?.id, setSelectedPeriod]);

    const handleCalendarDateChange = useCallback(
        (date?: Date) => {
            if (date) {
                setValue(date);
                setSelectedPeriod(getPeriodWithDate(allPeriods, date));
            }
        },
        [allPeriods, setSelectedPeriod],
    );

    const disablePrevButton = useMemo(() => {
        const currentPeriodIndex = allPeriods.findIndex(period => period.id === selectedPeriod?.id);
        return currentPeriodIndex === 0;
    }, [allPeriods, selectedPeriod?.id]);

    const disableNextButton = useMemo(() => {
        const currentPeriodIndex = allPeriods.findIndex(period => period.id === selectedPeriod?.id);
        return currentPeriodIndex === allPeriods.length - 1;
    }, [allPeriods, selectedPeriod?.id]);

    return (
        <>
            <div style={{ display: 'flex' }} ref={calendarButtonRef}>
                <MissingPeriodsNotification setSelectedPeriod={setSelectedPeriod} />
                <IconButton
                    iconProps={{ iconName: 'ChevronLeftSmall' }}
                    style={{ color: theme.semanticColors.bodyTextChecked }}
                    onClick={handlePrevPeriod}
                    disabled={disablePrevButton}
                />
                <DefaultButton
                    style={{ minWidth: 200 }}
                    text={periodName}
                    onClick={handleShowCallout}
                    styles={{ root: { marginRight: theme.spacing.s1, marginLeft: theme.spacing.s1 } }}
                />
                <IconButton
                    iconProps={{ iconName: 'ChevronRightSmall' }}
                    style={{ color: theme.semanticColors.bodyTextChecked }}
                    onClick={handleNextPeriod}
                    disabled={disableNextButton}
                />
            </div>
            {showCallout && (
                <Callout
                    styles={{ root: { maxWidth: 800, minWidth: 300 } }}
                    target={calendarButtonRef}
                    role="alertdialog"
                    gapSpace={0}
                    setInitialFocus
                    onDismiss={handleHideCallout}
                >
                    <div
                        style={{
                            height: '100%',
                            padding: '0 24px 20px',
                        }}
                    >
                        <Calendar
                            firstDayOfWeek={DayOfWeek.Monday}
                            value={value}
                            onSelectDate={handleCalendarDateChange}
                            strings={calendarStrings}
                            dateRangeType={DateRangeType.Day}
                            isDayPickerVisible={true}
                            highlightSelectedMonth={true}
                            showMonthPickerAsOverlay={false}
                            showGoToToday={false}
                            showWeekNumbers
                        />
                    </div>
                </Callout>
            )}
        </>
    );
};

const MissingPeriodsNotification = ({ setSelectedPeriod }: { setSelectedPeriod: (period: Period) => void }) => {
    const { t } = useTranslation();
    const { data: missingPeriods, refetch } = useQuery({
        queryKey: [MISSING_APPROVAL_PERIODS],
        queryFn: () => ApiCalls.getMissingApprovals().then(res => (res.data.length ? res.data : null)),
    });
    const buttonId = 'info-btn';
    const [showInfo, setShowInfo] = useState(false);
    useEffect(() => {
        if (missingPeriods) {
            setShowInfo(true);
            const timer = setTimeout(() => {
                setShowInfo(false);
            }, 2500);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [missingPeriods]);
    const [showMissingApprovals, setShowMissingApprovals] = useState(false);
    const getPeriodName = useTranslatePeriodName();
    const handleShowMissingApprovals = useCallback(() => {
        setShowMissingApprovals(true);
        refetch();
    }, [refetch]);
    const handleHideMissingApprovals = useCallback(() => setShowMissingApprovals(false), []);
    if (!missingPeriods) return null;
    return (
        <>
            <IconButton
                iconProps={{ iconName: 'Info' }}
                id={buttonId}
                onClick={handleShowMissingApprovals}
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
            />
            {showInfo && !showMissingApprovals && (
                <Callout directionalHint={DirectionalHint.topCenter} target={'#' + buttonId}>
                    {/* <Stack tokens={{ padding: 10 }}>Click here to see missing approvals</Stack> */}
                    <Stack tokens={{ padding: 10 }}>{t('approval:missingApprovals.tooltip')}</Stack>
                </Callout>
            )}
            {showMissingApprovals && (
                <Callout
                    directionalHint={DirectionalHint.bottomCenter}
                    target={'#' + buttonId}
                    onDismiss={handleHideMissingApprovals}
                    onMouseLeave={handleHideMissingApprovals}
                >
                    <Stack tokens={{ padding: 20, childrenGap: 10 }}>
                        {/* <Text variant="large">These periods needs approval</Text> */}
                        <Text variant="large">{t('approval:missingApprovals.approvalsListTitle')}</Text>
                        <Stack tokens={{ childrenGap: 10 }}>
                            {missingPeriods.map(period => (
                                <DefaultButton key={period.id} onClick={() => setSelectedPeriod(period)}>
                                    {getPeriodName(period)}
                                </DefaultButton>
                            ))}
                        </Stack>
                    </Stack>
                </Callout>
            )}
        </>
    );
};
